import { useRef, useState } from 'react';
import { isFileAccepted } from '../../util/util';
import { toast } from 'react-toastify';

export interface IFileUpload {
  setFiles: React.Dispatch<React.SetStateAction<File[]>>;
  accept: string;
  multiple?: boolean;
  note?: string;
}

/**
 * @author taulmax
 * @date 2024.10.11
 * @description 파일 업로드 공통 컴포넌트 - Tailwind CSS로 리뉴얼
 */
const FileUpload = ({ setFiles, accept, multiple = true, note }: IFileUpload) => {
  const [isDragging, setIsDragging] = useState<boolean>(false);
  const uploadRef = useRef<HTMLInputElement>(null);

  const onUploadFiles = (fileList: FileList) => {
    const fileArray = Array.from(fileList);
    const MAX_FILE_SIZE = 20 * 1024 * 1024; // 20MB

    if (!multiple && fileArray.length > 1) {
      toast.error('1개의 파일만 업로드 할 수 있습니다.');
      return;
    }

    for (const file of fileArray) {
      if (!isFileAccepted(accept, file.name)) {
        toast.error('파일 확장자가 맞지 않습니다.');
        return;
      }
      
      if (file.size > MAX_FILE_SIZE) {
        toast.error(`파일 크기는 20MB를 초과할 수 없습니다. (${file.name})`);
        return;
      }
    }

    if (!multiple) {
      setFiles(fileArray);
    } else {
      setFiles(prevFiles => [...prevFiles, ...fileArray]);
    }
  };

  const onDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
    onUploadFiles(e.dataTransfer.files);
  };

  const onClickUpload = () => {
    uploadRef.current?.click();
  };

  const preventDefault = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
  };

  const onDragEnter = () => setIsDragging(true);
  const onDragLeave = () => setIsDragging(false);

  return (
    <div
      className={`w-full h-full min-h-[200px] flex flex-col items-center justify-center cursor-pointer transition-all duration-200 border-2 border-dashed rounded-lg p-4 overflow-hidden ${
        isDragging 
          ? 'bg-blue-50 border-blue-400 scale-105' 
          : 'bg-gray-50 border-gray-300 hover:bg-gray-100 hover:border-gray-400'
      }`}
      onDrop={onDrop}
      onDragOver={preventDefault}
      onDragEnter={onDragEnter}
      onDragLeave={onDragLeave}
      onClick={onClickUpload}
    >
      <input
        ref={uploadRef}
        type="file"
        id="uploadInput"
        multiple={multiple}
        accept={accept}
        className="hidden"
        onChange={e => e.target.files && onUploadFiles(e.target.files)}
      />
      <svg 
        className="w-8 h-8 sm:w-12 sm:h-12 text-gray-400 mb-2 sm:mb-3 flex-shrink-0" 
        fill="none" 
        stroke="currentColor" 
        viewBox="0 0 24 24"
      >
        <path 
          strokeLinecap="round" 
          strokeLinejoin="round" 
          strokeWidth="1.5" 
          d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" 
        />
      </svg>
      <p className="text-sm sm:text-base font-medium text-gray-700 mb-1 text-center">파일 업로드</p>
      <p className="text-xs sm:text-sm text-gray-500 text-center">클릭하거나 드래그 앤 드롭하세요</p>
      {multiple && (
        <p className="text-xs text-gray-400 mt-2 text-center">여러 파일을 선택할 수 있습니다</p>
      )}
      <p className="text-xs sm:text-sm text-red-500 mt-2 text-center break-words max-w-full px-2">{note || '최대 20MB의 파일만 업로드 가능합니다.'}</p>
    </div>
  );
};

export default FileUpload;
